import { useEffect, useState } from 'react';
import JSZip from 'jszip';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import logoV from './assets/visionary-logo.svg';
import logo from './assets/blackfield-logo.svg';
import unityLogo from './assets/unity-logo.png';
import footerImg from './assets/blackfield_footer.webp';
import bf01 from './assets/blackfield_01.webp';
import bf02 from './assets/blackfield_02.webp';
import bf03 from './assets/blackfield_03.webp';
import bf04 from './assets/blackfield_04.webp';
import bf05 from './assets/blackfield_05.webp';
import bf06 from './assets/blackfield_06.webp';
import bf07 from './assets/blackfield_07.webp';
import emblWhiteSvg from './assets/blackfield_logo_emblem_white.svg';
import emblWhitePng from './assets/blackfield_logo_emblem_white.png';
import emblDarkSvg from './assets/blackfield_logo_emblem_dark.svg';
import emblDarkPng from './assets/blackfield_logo_emblem_dark.png';
import visionaryWhiteSvg from './assets/visionary_logo_white.svg';
import visionaryWhitePng from './assets/visionary_logo_white.png';
import squareSvg from './assets/blackfield™_square.svg';
import squarePng from './assets/blackfield™_square.png';
import roundedSvg from './assets/blackfield™_rounded.svg';
import roundedPng from './assets/blackfield™_rounded.png';

const SCREENSHOTS = [bf01, bf02, bf03, bf04, bf05, bf06, bf07];

const SOCIALS = [
  { label: 'Website', url: 'https://visionarygames.net' },
  { label: 'Steam', url: 'https://store.steampowered.com/app/3157920/Blackfield' },
  { label: 'YouTube', url: 'https://www.youtube.com/@blackfieldgame/videos' },
  { label: 'Patreon', url: 'https://www.patreon.com/Blackfield' },
  { label: 'Facebook', url: 'https://www.facebook.com/profile.php?id=61564231462506' },
  { label: 'Reddit', url: 'https://www.reddit.com/r/BlackfieldGame/' },
  { label: 'Instagram', url: 'https://www.instagram.com/blackfieldgame/' },
  { label: 'Discord', url: 'https://discord.gg/dtkSt7qr' },
  { label: 'Telegram', url: 'https://t.me/+hfqMmAgPY_84ZTE0' },
  { label: 'WhatsApp', url: 'https://chat.whatsapp.com/IorjnA4B5QzBWO63Jor30V' },
  { label: 'Threads', url: 'https://www.threads.net/@blackfieldgame' },
  { label: 'X / Twitter', url: 'https://x.com/blackfieldgame' },
  { label: 'Medium', url: 'https://medium.com/@blackfield' },
  { label: 'TikTok', url: 'https://www.tiktok.com/@blackfieldgame' },
  { label: 'Apple Podcast', url: 'https://podcasts.apple.com/il/podcast/blackfield/id1762277742' },
];

const MEDIA = [
  { source: 'Pocket Gamer', title: '"Blackfield Coming Soon"', url: 'https://www.pocketgamer.com/blackfield/coming-soon/' },
  { source: 'Game Developers Network', title: '"Visionary Games Develop Limbo-esque Indie Adventure Game Blackfield"', url: 'https://www.gamedevelopersnetwork.biz/news/view/visionary-games-develop-limbo-esque-indie-adventure-game-blackfield' },
  { source: 'Mobi.gg', title: '"Blackfield Announcement Mobile"', url: 'https://mobi.gg/en/news/blackfield-announcement-mobile/' },
];

const LOGOS = [
  { file: logo,           name: 'blackfield-logo.svg' },
  { file: logoV,          name: 'visionary-games-logo.svg' },
  { file: emblWhiteSvg,   name: 'blackfield-emblem-white.svg' },
  { file: emblWhitePng,   name: 'blackfield-emblem-white.png' },
  { file: emblDarkSvg,    name: 'blackfield-emblem-dark.svg' },
  { file: emblDarkPng,    name: 'blackfield-emblem-dark.png' },
  { file: visionaryWhiteSvg, name: 'visionary-logo-white.svg' },
  { file: visionaryWhitePng, name: 'visionary-logo-white.png' },
  { file: squareSvg,      name: 'blackfield-square.svg' },
  { file: squarePng,      name: 'blackfield-square.png' },
  { file: roundedSvg,     name: 'blackfield-rounded.svg' },
  { file: roundedPng,     name: 'blackfield-rounded.png' },
];

async function downloadPressKit(setDownloading) {
  setDownloading(true);
  try {
    const zip = new JSZip();
    const screenshots = zip.folder('screenshots');
    const logos = zip.folder('logos');

    const fetchBlob = (url) => fetch(url).then((r) => r.blob());

    await Promise.all([
      ...SCREENSHOTS.map((url, i) =>
        fetchBlob(url).then((blob) =>
          screenshots.file(`blackfield_screenshot_${String(i + 1).padStart(2, '0')}.png`, blob)
        )
      ),
      ...LOGOS.map(({ file, name }) =>
        fetchBlob(file).then((blob) => logos.file(name, blob))
      ),
    ]);

    const content = await zip.generateAsync({ type: 'blob' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(content);
    a.download = 'blackfield-press-kit.zip';
    a.click();
    URL.revokeObjectURL(a.href);
  } finally {
    setDownloading(false);
  }
}

export default function PressKitPage() {
  const publisherAuthed = sessionStorage.getItem('bf_gate_auth') === '1';
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="article-page">

      <Helmet>
        <title>Press Kit – Blackfield | Visionary Games</title>
        <meta name="description" content="Official press kit for Blackfield — a dark survival adventure game by Visionary Games. Logos, screenshots, game info, and media links." />
        <link rel="canonical" href="https://visionarygames.net/press-kit" />
        <meta property="og:title" content="Press Kit – Blackfield | Visionary Games" />
        <meta property="og:description" content="Official press kit for Blackfield — a dark survival adventure game by Visionary Games." />
        <meta property="og:url" content="https://visionarygames.net/press-kit" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://visionarygames.net/android-chrome-512x512.png" />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Press Kit – Blackfield | Visionary Games" />
        <meta name="twitter:description" content="Official press kit for Blackfield — a dark survival adventure game by Visionary Games." />
        <meta name="twitter:image" content="https://visionarygames.net/android-chrome-512x512.png" />
      </Helmet>

      {/* ── Top Nav ─────────────────────────────────────── */}
      <nav className="top-nav top-nav-solid">
        <div className="top-nav-links">
          <Link to="/" className="top-nav-link top-nav-home">HOME</Link>
          <Link to="/news" className="top-nav-link">NEWS</Link>
          <Link to="/press-kit" className="top-nav-link">PRESS KIT</Link>
          <a href="mailto:yuri@blackfieldgame.com" className="top-nav-link">CONTACT</a>
          <span className="top-nav-link top-nav-disabled">ART BOOK</span>
          <Link to="/publisher" className="top-nav-link top-nav-presentation"><span className="material-symbols-rounded" style={{fontSize:'0.85rem',verticalAlign:'middle'}}>{publisherAuthed ? 'lock_open' : 'lock'}</span> PUBLISHER</Link>
        </div>
      </nav>

      {/* ── Content ─────────────────────────────────────── */}
      <div className="feed-wrap">
        <h1 className="feed-title">PRESS KIT</h1>

        {/* About */}
        <section className="pk-section pk-section-first">
          <h2 className="pk-heading">About</h2>
          <div className="pk-facts">
            {[
              ['Developer', 'Visionary Games'],
              ['Publisher', 'Visionary Games'],
              ['Game Designer', 'Osadchy Yuri'],
              ['Title', 'Blackfield'],
              ['Tagline', '"Survival Across Parallel Realms"'],
              ['Genre', 'Action, Adventure, Indie'],
              ['Engine', 'Unity'],
              ['Platforms', 'PC, Mac, Mobile'],
              ['Players', '1 (Single-player)'],
              ['Languages', 'English'],
              ['Rating', 'Adult'],
              ['Release Date', 'Q4 2026'],
            ].map(([k, v]) => (
              <div key={k} className="pk-fact-row">
                <span className="pk-fact-key">{k}</span>
                <span className="pk-fact-val">{v}</span>
              </div>
            ))}
          </div>
        </section>

        {/* About the game */}
        <section className="pk-section">
          <h2 className="pk-heading">About Blackfield</h2>
          <p className="pk-text">
            In a world similar to ours, in a distant galaxy where humanity grapples with severe famine,
            8-year-old Marta must choose between surviving to find her parents or helping those around her.
          </p>
        </section>

        {/* Descriptions */}
        <section className="pk-section">
          <h2 className="pk-heading">Descriptions</h2>
          <h3 className="pk-subheading">Short</h3>
          <p className="pk-text">
            In a shadowed parallel universe, 8-year-old Marta navigates desolate landscapes ruled by
            mechanical overlords and nightmarish creatures, desperately seeking her lost family while
            survival becomes a relentless test of wits and willpower.
          </p>
          <h3 className="pk-subheading">Long</h3>
          <p className="pk-text">
            In a shadowed parallel universe, where the remnants of humanity cling to existence amidst
            mechanical overlords and nightmarish creatures, a relentless battle for dwindling food and
            energy supplies rages. Marta, driven by hope of finding her lost family, journeys through
            desolate landscapes and haunted ruins. Every step blurs ally and enemy lines, turning survival
            into a test of wits and willpower.
          </p>
        </section>

        {/* Key Features */}
        <section className="pk-section">
          <h2 className="pk-heading">Key Features</h2>
          <ul className="pk-list">
            <li><strong>Survival-driven narrative</strong> — Navigate a parallel universe where robots, bizarre creatures, and humans vie for scarce resources. Marta's family quest plays out amid shifting alliances.</li>
            <li><strong>Resource management & strategy</strong> — Manage limited food and energy. Tough choices on alliances and allocation shape Marta's path and others' fates.</li>
            <li><strong>Dynamic faction relationships</strong> — Forge alliances or rivalries with robots, creatures, and human factions — each with distinct goals. Choices influence faction dynamics and narrative.</li>
            <li><strong>Innovative survival mechanics</strong> — Adapt to fluctuating environments and plan strategically across diverse landscapes to uncover clues about Marta's family.</li>
            <li><strong>Emotional depth & exploration</strong> — Explore atmospheric regions reflecting struggle and resilience while following Marta's journey through loss, courage, and hope.</li>
          </ul>
        </section>

        {/* Download button */}
        <div className="pk-download-wrap">
          <button
            className="pk-download-btn"
            onClick={() => downloadPressKit(setDownloading)}
            disabled={downloading}
          >
            {downloading
              ? 'PREPARING…'
              : 'DOWNLOAD PRESS KIT PACK'}
          </button>
        </div>

        {/* Screenshots */}
        <section className="pk-section">
          <h2 className="pk-heading">Screenshots</h2>
          <div className="pk-screenshots">
            {SCREENSHOTS.map((img, i) => (
              <a key={i} href={img} target="_blank" rel="noopener noreferrer" className="pk-screenshot-wrap">
                <img src={img} alt={`Blackfield screenshot ${i + 1}`} className="pk-screenshot" />
              </a>
            ))}
          </div>
        </section>

        {/* Trailer */}
        <section className="pk-section">
          <h2 className="pk-heading">Trailer</h2>
          <div className="pk-video-wrap">
            <iframe
              src="https://www.youtube.com/embed/owUpflFnaNQ"
              title="Blackfield Trailer"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="pk-video"
            />
          </div>
        </section>

        {/* Logos */}
        <section className="pk-section">
          <h2 className="pk-heading">Logos</h2>
          <div className="pk-logos-dl">
            <img src={logo} alt="Blackfield" className="pk-logo-dl" />
            <img src={logoV} alt="Visionary Games" className="pk-logo-dl" />
            <div className="pk-logos-row">
              <img src={squarePng} alt="Blackfield Square" className="pk-logo-icon" />
              <img src={roundedPng} alt="Blackfield Rounded" className="pk-logo-icon" />
            </div>
          </div>
        </section>

        {/* Online Presence */}
        <section className="pk-section">
          <h2 className="pk-heading">Online Presence</h2>
          <div className="pk-links">
            {SOCIALS.map(({ label, url }) => (
              <a key={label} href={url} target="_blank" rel="noopener noreferrer" className="pk-link">
                <span className="pk-link-label">{label}</span>
                <span className="pk-link-url">{url.replace('https://', '')}</span>
              </a>
            ))}
          </div>
        </section>

        {/* News & Media */}
        <section className="pk-section">
          <h2 className="pk-heading">News &amp; Media</h2>
          <div className="pk-media">
            {MEDIA.map(({ source, title, url }) => (
              <a key={source} href={url} target="_blank" rel="noopener noreferrer" className="pk-media-item">
                <span className="pk-media-source">{source}</span>
              </a>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section className="pk-section pk-section-last">
          <h2 className="pk-heading">Contact</h2>
          <a href="mailto:yuri@blackfieldgame.com" className="pk-contact-email">yuri@blackfieldgame.com</a>
        </section>

      </div>

      {/* ── Footer ───────────────────────────────────────── */}
      <div className="site-footer-wrap">
        <img src={footerImg} alt="" className="blackfield-footer-img" />
        <div className="site-footer-overlay">
          <img src={logoV} alt="VISIONARY GAMES" className="footer-logo" />
          <span className="footer-credit">A OSADCHY YURI GAME</span>
          <img src={unityLogo} alt="Made with Unity" className="footer-unity-img" />
        </div>
      </div>

    </div>
  );
}
